import { Service } from './Service';
import http from './ApiConfig';
import {
  CampoValorType,
  CampoPersonalizadoType,
  QuadroPersonalizadoOutputType,
  QuadroPersonalizadoInputType,
  QuadroPersonalizadoUpdateType,
  DadoQuadroPersonalizadoOutputType,
  DadoQuadroPersonalizadoInputType,
  DadoQuadroPersonalizadoUpdateType,
  TipoConsultaDisponivelType,
} from '@/types/quadroPersonalizado';

export class QuadroPersonalizadoService extends Service {
  constructor() {
    super('QuadrosPersonalizados');
  }

  public async obterTodos(): Promise<QuadroPersonalizadoOutputType[]> {
    const response = await http.get(`${this._nomeControle}`, this.GetHeader());
    return response.data;
  }

  public async obterPorId(id: string): Promise<QuadroPersonalizadoOutputType> {
    const response = await http.get(`${this._nomeControle}/${id}`, this.GetHeader());
    return response.data;
  }

  public async criar(quadro: QuadroPersonalizadoInputType): Promise<QuadroPersonalizadoOutputType> {
    const response = await http.post(`${this._nomeControle}`, quadro, this.GetHeader());
    return response.data;
  }

  public async atualizar(id: string, quadro: QuadroPersonalizadoUpdateType): Promise<QuadroPersonalizadoOutputType> {
    const response = await http.put(`${this._nomeControle}/${id}`, quadro, this.GetHeader());
    return response.data;
  }

  public async excluir(id: string): Promise<void> {
    await http.delete(`${this._nomeControle}/${id}`, this.GetHeader());
  }

  public async verificarDadosVinculados(quadroId: string): Promise<boolean> {
    try {
      const response = await http.get(`${this._nomeControle}/${quadroId}/verificar-dados-vinculados`, this.GetHeader());
      return response.data.temDados;
    } catch (error: any) {
      return false;
    }
  }

  public async buscarPorNome(nome: string): Promise<QuadroPersonalizadoOutputType[]> {
    const response = await http.get(`${this._nomeControle}/buscar?nome=${encodeURIComponent(nome)}`, this.GetHeader());
    return response.data;
  }

  public async obterTiposConsultaDisponiveis(clinicaId: number): Promise<TipoConsultaDisponivelType[]> {
    const response = await http.get(`${this._nomeControle}/tipos-consulta-disponiveis/${clinicaId}`, this.GetHeader());
    return response.data;
  }

  public async obterPorTipoConsulta(tipoConsultaId: number): Promise<any> {
    try {
      const response = await http.get(`${this._nomeControle}/por-tipo-consulta/${tipoConsultaId}`, this.GetHeader());
      return response.data ? { data: response.data } : { data: null };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { data: null };
      }
      throw error;
    }
  }

  public async verificarNomeExiste(nome: string, idExcluir?: string): Promise<boolean> {
    try {
      const response = await http.get(
        `${this._nomeControle}/verificar-nome?nome=${encodeURIComponent(nome)}${idExcluir ? `&idExcluir=${idExcluir}` : ''}`,
        this.GetHeader(),
      );
      return response.data.existe;
    } catch (error: any) {
      return false;
    }
  }

  public async obterDadoPorEntidade(
    quadroId: string,
    entidadeId: string,
    tipoEntidade: string,
  ): Promise<DadoQuadroPersonalizadoOutputType | null> {
    try {
      const response = await http.get(
        `${this._nomeControle}/${quadroId}/dados/${entidadeId}?tipoEntidade=${encodeURIComponent(tipoEntidade)}`,
        this.GetHeader(),
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  public async obterDadosPorEntidade(
    entidadeId: string,
    tipoEntidade: string,
  ): Promise<DadoQuadroPersonalizadoOutputType[]> {
    const response = await http.get(
      `${this._nomeControle}/dados/entidade/${entidadeId}?tipoEntidade=${encodeURIComponent(tipoEntidade)}`,
      this.GetHeader(),
    );
    return response.data;
  }

  public async salvarDado(dado: DadoQuadroPersonalizadoInputType): Promise<DadoQuadroPersonalizadoOutputType> {
    const response = await http.post(`${this._nomeControle}/dados`, dado, this.GetHeader());
    return response.data;
  }

  public async atualizarDado(id: string, dado: DadoQuadroPersonalizadoUpdateType): Promise<DadoQuadroPersonalizadoOutputType> {
    const response = await http.put(`${this._nomeControle}/dados/${id}`, dado, this.GetHeader());
    return response.data;
  }

  public async salvarDadoEstruturado(
    quadroPersonalizadoId: string,
    entidadeId: string,
    tipoEntidade: string,
    dadosEstruturados: CampoValorType[],
    atendimentoId?: number,
  ): Promise<DadoQuadroPersonalizadoOutputType> {
    const dado: DadoQuadroPersonalizadoInputType = {
      quadroPersonalizadoId,
      entidadeId,
      tipoEntidade,
      atendimentoId,
      dadosEstruturados,
    };

    return this.salvarDado(dado);
  }

  public async atualizarDadoEstruturado(
    id: string,
    dadosEstruturados: CampoValorType[],
    atendimentoId?: number,
  ): Promise<DadoQuadroPersonalizadoOutputType> {
    const dado: DadoQuadroPersonalizadoUpdateType = {
      id,
      atendimentoId,
      dadosEstruturados,
    };

    return this.atualizarDado(id, dado);
  }

  public async salvarDadoCompatibilidade(
    quadroPersonalizadoId: string,
    entidadeId: string,
    tipoEntidade: string,
    dadosAntigos: { [key: string]: any },
    campos: CampoPersonalizadoType[],
    atendimentoId?: number,
  ): Promise<DadoQuadroPersonalizadoOutputType> {
    const dadosEstruturados = this.converterDadosAntigos(dadosAntigos, campos);

    const dado: DadoQuadroPersonalizadoInputType = {
      quadroPersonalizadoId,
      entidadeId,
      tipoEntidade,
      atendimentoId,
      dadosEstruturados,
    };

    return this.salvarDado(dado);
  }

  public async atualizarDadoCompatibilidade(
    id: string,
    dadosAntigos: { [key: string]: any },
    campos: CampoPersonalizadoType[],
    atendimentoId?: number,
  ): Promise<DadoQuadroPersonalizadoOutputType> {
    const dadosEstruturados = this.converterDadosAntigos(dadosAntigos, campos);

    const dado: DadoQuadroPersonalizadoUpdateType = {
      id,
      atendimentoId,
      dadosEstruturados,
    };

    return this.atualizarDado(id, dado);
  }

  public async excluirDado(id: string): Promise<void> {
    await http.delete(`${this._nomeControle}/dados/${id}`, this.GetHeader());
  }

  public converterDadosAntigos(dadosAntigos: { [key: string]: any }, campos: CampoPersonalizadoType[]): CampoValorType[] {
    const dadosConvertidos: CampoValorType[] = [];
    const dadosLimpos = this.limparValueKindDosDados(dadosAntigos);

    Object.entries(dadosLimpos).forEach(([chave, valor]) => {
      dadosConvertidos.push({
        nomeDoCampo: chave,
        valor: this.converterParaString(valor),
      });
    });

    return dadosConvertidos;
  }

  private converterParaString(valor: any): string {
    if (valor === null || valor === undefined) {
      return '';
    }

    if (typeof valor === 'string') {
      return valor;
    }

    if (typeof valor === 'number' || typeof valor === 'boolean') {
      return String(valor);
    }

    if (typeof valor === 'object') {
      return JSON.stringify(valor);
    }

    return String(valor);
  }

  private limparValueKindDosDados(dados: any): { [key: string]: any } {
    if (!dados || typeof dados !== 'object') {
      return {};
    }

    const dadosLimpos: { [key: string]: any } = {};

    Object.keys(dados).forEach((chave) => {
      const valor = dados[chave];

      if (valor && typeof valor === 'object' && 'ValueKind' in valor) {
        switch (valor.ValueKind) {
          case 3: // String
            dadosLimpos[chave] = '';
            break;
          case 4: // Number
            dadosLimpos[chave] = 0;
            break;
          case 5: // Boolean
            dadosLimpos[chave] = false;
            break;
          default:
            dadosLimpos[chave] = null;
        }
      } else if (valor && typeof valor === 'object' && !Array.isArray(valor)) {
        dadosLimpos[chave] = JSON.stringify(valor);
      } else {
        dadosLimpos[chave] = valor;
      }
    });

    return dadosLimpos;
  }

  public converterParaDadosAntigos(dados: CampoValorType[]): { [key: string]: any } {
    const dadosAntigos: { [key: string]: any } = {};

    dados.forEach((item) => {
      dadosAntigos[item.nomeDoCampo] = item.valor;
    });

    return dadosAntigos;
  }

  public extrairDadosDoOutput(output: DadoQuadroPersonalizadoOutputType, campos: CampoPersonalizadoType[]): { [key: string]: any } {
    if (output.dadosEstruturados && output.dadosEstruturados.length > 0) {
      return this.converterParaDadosAntigos(output.dadosEstruturados);
    }

    return {};
  }

  public static gerarGuidEntidade(entidadeId: number, tipoEntidade: string): string {
    const chave = `${tipoEntidade}_${entidadeId}`;
    let hash = 0;

    for (let i = 0; i < chave.length; i++) {
      const char = chave.charCodeAt(i);
      hash = ((hash * 31) + char) % 0x7fffffff;
    }

    const hex = Math.abs(hash).toString(16).padStart(8, '0');
    const part1 = hex.substring(0, 8);
    const part2 = hex.substring(0, 4);
    const part3 = `4${hex.substring(1, 4)}`;
    const part4 = `a${hex.substring(4, 7)}`;
    const part5 = hex.padEnd(12, '0').substring(0, 12);
    return `${part1}-${part2}-${part3}-${part4}-${part5}`;
  }
}
